import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import { ClothesImage, CollectionImage } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { Text } from '~/components/ui/Text';
import { useZoomFactor } from '~/hooks';
import { rage } from '~/rage';
import { IPromoCodeAdmin } from '~/rage/services/PromoService/promoServiceTypes';
import {
	IModalReplyDefault,
	isClothesItem,
	ModalButtonColor,
	ModalType,
	RewardType,
} from '~/shared';
import caseImage from '../../../mainMenu/assets/images/case.png';
import cardSoftImage from '../../../mainMenu/assets/images/cases/card_soft.png';
import expImage from '../../../mainMenu/assets/images/exp.svg';
import { HeaderContainer, Title } from '../../components/Header';
import { PanelContainer } from '../../components/Panel';
import { PortalHint } from '../../components/PortalHint';
import { useLayoutDispatch } from '../../redux/hooks';
import { setCurrentView } from '../../redux/slices/promoSlice';
import {
	formatDate,
	formatRewardDisplay,
	getCollectionForType,
	getRewardId,
	REWARD_TYPE_LABELS,
} from '../../utils/rewardHelpers';
import {
	ContentContainer,
	StyledActivationCell,
	StyledDeleteButton,
	StyledEmptyText,
	StyledListColumn,
	StyledLoadingText,
	StyledPromoCode,
	StyledPromoRewardsContainer,
	StyledPromoTable,
	StyledRewardImage,
	StyledRewardImageWrapper,
	StyledRewardsCell,
	StyledStatusBadge,
	StyledStatusCell,
	StyledTableCell,
	StyledTableRow,
	StyledText,
	StyledTimeCell,
	StyledValidationError,
} from './PromoList.styled';

const PromoList: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const [promoCodes, setPromoCodes] = useState<IPromoCodeAdmin[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [deleteButtonHover, setDeleteButtonHover] = useState<string | null>(
		null,
	);
	const [hoveredReward, setHoveredReward] = useState<{
		promoCode: string;
		rewardIndex: number;
	} | null>(null);
	const [hoveredDatePromo, setHoveredDatePromo] = useState<string | null>(null);
	const [hintPosition, setHintPosition] = useState({ top: 0, left: 0 });
	const [dateHintPosition, setDateHintPosition] = useState({ top: 0, left: 0 });
	const zoomFactor = useZoomFactor();

	const rewardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
	const statusBadgeRefs = useRef<Map<string, HTMLDivElement>>(new Map());

	const showRewardHint = (promoCode: string, rewardIndex: number) => {
		const key = `${promoCode}-${rewardIndex}`;
		const el = rewardRefs.current.get(key);
		if (el) {
			const rect = el.getBoundingClientRect();
			setHintPosition({
				top: rect.top * (1 / zoomFactor) - 8,
				left:
					rect.left * (1 / zoomFactor) + (rect.width * (1 / zoomFactor)) / 2,
			});
		}
		setHoveredReward({ promoCode, rewardIndex });
	};

	const hideRewardHint = () => {
		setHoveredReward(null);
	};

	const showDateHint = (code: string) => {
		const el = statusBadgeRefs.current.get(code);
		if (el) {
			const rect = el.getBoundingClientRect();
			setDateHintPosition({
				top: rect.top * (1 / zoomFactor) - 8,
				left:
					rect.left * (1 / zoomFactor) + (rect.width * (1 / zoomFactor)) / 2,
			});
		}
		setHoveredDatePromo(code);
	};

	const hideDateHint = () => {
		setHoveredDatePromo(null);
	};

	const fetchPromoCodes = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const codes = await rage.promo.fetchAllPromoCodes();
			setPromoCodes(codes);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Ошибка загрузки промокодов',
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchPromoCodes();
	}, [fetchPromoCodes]);

	const handleClose = useCallback(() => {
		rage.promo.exitMenu();
	}, []);

	const handleGoToAdd = useCallback(() => {
		dispatch(setCurrentView('add'));
	}, [dispatch]);

	const handleDelete = useCallback(
		(code: string) => {
			rage.modal.showModal(
				{
					type: ModalType.Default,
					title: 'Подтверждение',
					text: `Вы уверены, что хотите удалить промокод "${code}"?`,
					acceptButton: { text: 'Удалить', color: ModalButtonColor.Danger },
					cancelButton: { text: 'Отменить', color: ModalButtonColor.Secondary },
				},
				async (result: IModalReplyDefault) => {
					if (result.isAccepted) {
						try {
							await rage.promo.deletePromoCode(code);
							rage.notifications.showSuccess(`Промокод "${code}" удален`);
							fetchPromoCodes();
						} catch (err) {
							rage.notifications.showError(
								err instanceof Error ? err.message : 'Ошибка удаления',
							);
						}
					}
				},
			);
		},
		[fetchPromoCodes],
	);

	const isPromoActive = (promo: IPromoCodeAdmin): boolean => {
		const now = Date.now();
		const hasStarted =
			!promo.enabledTimestampStart || promo.enabledTimestampStart < now;
		const hasNotEnded =
			!promo.enabledTimestampEnd || promo.enabledTimestampEnd > now;
		return hasStarted && hasNotEnded;
	};

	const getRewardHintName = (type: RewardType, value: string): string => {
		return formatRewardDisplay(type, value, (num) => {
			const parsed = parseInt(String(num));
			return isNaN(parsed) ? String(num) : parsed.toLocaleString('ru-RU');
		});
	};

	return (
		<PanelContainer $thinner>
			{hoveredDatePromo &&
				createPortal(
					<CSSTransition
						timeout={300}
						classNames="hint"
						in={!!hoveredDatePromo}
						unmountOnExit
					>
						<PortalHint
							style={{
								top: dateHintPosition.top,
								left: dateHintPosition.left,
								transform: 'translate(-50%, -100%)',
							}}
						>
							{(() => {
								const promo = promoCodes.find(
									(p) => p.code === hoveredDatePromo,
								);
								if (!promo) return null;
								if (
									!promo.enabledTimestampStart &&
									!promo.enabledTimestampEnd
								) {
									return 'Бессрочно';
								}
								if (!promo.enabledTimestampStart) {
									return `до ${formatDate(promo.enabledTimestampEnd)}`;
								}
								if (!promo.enabledTimestampEnd) {
									return `${formatDate(
										promo.enabledTimestampStart,
									)} - бессрочно`;
								}
								return `${formatDate(
									promo.enabledTimestampStart,
								)} - ${formatDate(promo.enabledTimestampEnd)}`;
							})()}
						</PortalHint>
					</CSSTransition>,
					document.body,
				)}
			{hoveredReward &&
				createPortal(
					<CSSTransition
						timeout={300}
						classNames="hint"
						in={!!hoveredReward}
						unmountOnExit
					>
						<PortalHint
							style={{
								top: hintPosition.top,
								left: hintPosition.left,
								transform: 'translate(-50%, -100%)',
							}}
						>
							{(() => {
								const promo = promoCodes.find(
									(p) => p.code === hoveredReward?.promoCode,
								);
								if (!promo) return null;
								const reward = promo.rewards[hoveredReward.rewardIndex];
								if (!reward) return null;
								return getRewardHintName(reward.type, reward.value);
							})()}
						</PortalHint>
					</CSSTransition>,
					document.body,
				)}
			<HeaderContainer>
				<Title>Список промокодов</Title>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height30}
					onClick={handleGoToAdd}
					style={{
						position: 'absolute',
						right: 55,
						top: '50%',
						transform: 'translateY(-50%)',
						width: 'auto',
						padding: '0 12px',
					}}
				>
					<Text style={{ fontSize: 12 }}>Добавить</Text>
				</Button>
				<CloseButton height={ButtonHeight.Height30} onClick={handleClose} />
			</HeaderContainer>
			<ContentContainer>
				{loading ? (
					<StyledLoadingText>Загрузка промокодов...</StyledLoadingText>
				) : error ? (
					<StyledValidationError>{error}</StyledValidationError>
				) : promoCodes.length === 0 ? (
					<StyledEmptyText>Нет промокодов</StyledEmptyText>
				) : (
					<StyledListColumn>
						<StyledPromoTable cellSpacing={0} cellPadding={0}>
							{promoCodes.map((promo) => {
								const isActive = isPromoActive(promo);
								const activationText = promo.maxActivations
									? `${promo.activationCount}/${promo.maxActivations}`
									: `${promo.activationCount}/∞`;
								return (
									<StyledTableRow key={promo.code}>
										<StyledTableCell>
											<StyledPromoCode>{promo.code}</StyledPromoCode>
										</StyledTableCell>
										<StyledStatusCell>
											<StyledStatusBadge
												ref={(el) => {
													if (el) statusBadgeRefs.current.set(promo.code, el);
													else statusBadgeRefs.current.delete(promo.code);
												}}
												$active={isActive}
												onMouseEnter={() => showDateHint(promo.code)}
												onMouseLeave={hideDateHint}
											>
												<Text>{isActive ? 'Активный' : 'Неактивный'}</Text>
											</StyledStatusBadge>
										</StyledStatusCell>
										<StyledActivationCell>
											<StyledText>{activationText}</StyledText>
										</StyledActivationCell>
										<StyledTimeCell>
											<StyledText>{promo.requiredPlayTimeMin} мин</StyledText>
										</StyledTimeCell>
										<StyledRewardsCell>
											<StyledPromoRewardsContainer>
												{promo.rewards.map((reward, idx) => {
													const collection = getCollectionForType(reward.type);
													const rewardId = getRewardId(
														reward.type,
														reward.value,
													);
													const key = `${promo.code}-${idx}`;

													return (
														<StyledRewardImageWrapper
															key={key}
															ref={(el) => {
																if (el) rewardRefs.current.set(key, el);
																else rewardRefs.current.delete(key);
															}}
															onMouseEnter={() =>
																showRewardHint(promo.code, idx)
															}
															onMouseLeave={hideRewardHint}
														>
															<StyledRewardImage>
																{reward.type === RewardType.MONEY ? (
																	<img
																		src={cardSoftImage}
																		width={36}
																		height={36}
																	/>
																) : reward.type === RewardType.EXP ? (
																	<img src={expImage} width={36} height={36} />
																) : reward.type === RewardType.VEHICLE ? (
																	(() => {
																		const imageId = rage.cases.getRewardImageId(
																			getRewardId(reward.type, reward.value) ||
																				'',
																		);
																		if (!imageId)
																			return (
																				<Text
																					style={{
																						fontSize: 9,
																						color: 'rgba(255,255,255,0.4)',
																					}}
																				>
																					ТС
																				</Text>
																			);
																		return (
																			<CollectionImage
																				collection="vehicles"
																				id={imageId as never}
																				size={36}
																			/>
																		);
																	})()
																) : reward.type === RewardType.LOOTBOX ||
																  reward.type === RewardType.BUNDLE ? (
																	<img src={caseImage} width={36} height={36} />
																) : collection &&
																  rewardId &&
																  reward.type === RewardType.ITEM &&
																  isClothesItem(rewardId) ? (
																	<ClothesImage id={rewardId} size={36} />
																) : collection && rewardId ? (
																	<CollectionImage
																		collection="items"
																		id={rewardId as never}
																		size={36}
																	/>
																) : (
																	<Text
																		style={{
																			fontSize: 9,
																			color: 'rgba(255,255,255,0.4)',
																			textAlign: 'center',
																		}}
																	>
																		{REWARD_TYPE_LABELS[reward.type].slice(
																			0,
																			3,
																		)}
																	</Text>
																)}
															</StyledRewardImage>
														</StyledRewardImageWrapper>
													);
												})}
											</StyledPromoRewardsContainer>
										</StyledRewardsCell>
										<StyledTableCell>
											<StyledDeleteButton
												$hover={deleteButtonHover === promo.code}
												onClick={() => handleDelete(promo.code)}
												onMouseEnter={() => setDeleteButtonHover(promo.code)}
												onMouseLeave={() => setDeleteButtonHover(null)}
											/>
										</StyledTableCell>
									</StyledTableRow>
								);
							})}
						</StyledPromoTable>
					</StyledListColumn>
				)}
			</ContentContainer>
		</PanelContainer>
	);
};

export { PromoList };
